import { useState, useCallback, useEffect, useMemo } from "react";
import { Button, Dialog, Flex, Text, TextField } from "@radix-ui/themes";
import {
  TenantBrand,
  UpsertBrandInputDto,
} from "@/v2/entities/brand/Brand.model";
import { useBrandApi } from "@/v2/entities/brand/Brand.api";

interface EditBrandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brand: TenantBrand;
  refetch: () => void;
}

export function EditBrandDialog({
  open,
  onOpenChange,
  brand,
  refetch,
}: EditBrandDialogProps) {
  const brandApi = useBrandApi();

  const [formData, setFormData] = useState<UpsertBrandInputDto>({
    name: brand.name,
    subdomain: brand.subdomain,
  });
  const [errors, setErrors] = useState<Partial<UpsertBrandInputDto>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setFormData({
        name: brand.name,
        subdomain: brand.subdomain,
      });
      setErrors({});
    }
  }, [open, brand]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<UpsertBrandInputDto> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Brand name is required";
    }

    if (!formData.subdomain.trim()) {
      newErrors.subdomain = "Subdomain is required";
    } else if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
      newErrors.subdomain =
        "Subdomain can only contain lowercase letters, numbers, and hyphens";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await brandApi.updateBrand({
        externalBrandId: brand.externalId,
        data: formData,
      });
      onOpenChange(false);
      refetch();
    } catch (error) {
      // error is handled by the mutation
      // eslint-disable-next-line no-console
      console.error("failed to update brand:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    formData,
    validateForm,
    brandApi,
    brand.externalId,
    onOpenChange,
    refetch,
  ]);

  const handleCancel = useCallback(() => {
    setFormData({
      name: brand.name,
      subdomain: brand.subdomain,
    });
    setErrors({});
    onOpenChange(false);
  }, [brand, onOpenChange]);

  const handleFieldChange = useCallback(
    (field: keyof UpsertBrandInputDto, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error for this field when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors]
  );

  const hasChanges = useMemo(
    () =>
      formData.name !== brand.name || formData.subdomain !== brand.subdomain,
    [formData, brand]
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="450px">
        <Dialog.Title>Edit Brand</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Update the brand information.
        </Dialog.Description>

        <Flex direction="column" gap="3">
          <label>
            <Text as="div" size="2" mb="1" weight="bold">
              Brand Name
            </Text>
            <TextField.Root
              placeholder="Enter brand name"
              value={formData.name}
              onChange={(e) => handleFieldChange("name", e.target.value)}
            />
            {errors.name && (
              <Text size="1" color="red" mt="1">
                {errors.name}
              </Text>
            )}
          </label>

          <label>
            <Text as="div" size="2" mb="1" weight="bold">
              Subdomain
            </Text>
            <TextField.Root
              placeholder="Enter subdomain (e.g., mybrand)"
              value={formData.subdomain}
              onChange={(e) =>
                handleFieldChange("subdomain", e.target.value.toLowerCase())
              }
            />
            {errors.subdomain && (
              <Text size="1" color="red" mt="1">
                {errors.subdomain}
              </Text>
            )}
          </label>

          <Text size="1" color="gray">
            External ID: <span className="font-mono">{brand.externalId}</span>
          </Text>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" onClick={handleCancel}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            onClick={handleSubmit}
            highContrast
            loading={isLoading}
            disabled={!hasChanges}
          >
            Update Brand
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
